/**
 * Kennzahlenleiste der Projektübersicht. Seit 13.10.2026 standardmäßig
 * ausgeblendet (Benjamin: „Die Zahlen müssen standardmäßig ausgeblendet sein") —
 * am Rechner lässt sie sich einblenden, am Telefon gibt es sie nicht.
 * Basis ist der aktive Reiter, nicht der Suchfilter.
 */
import { useMemo } from "react";
import type { ProjektUebersicht } from "../../lib/types";
import { STATUS_ID_ORDER, canonicalProjektStatus, computeGewinn, fmtEUR, statusIdFor } from "../../lib/projekte";

export default function ProjekteKennzahlen({ projekte, statusOpts }: { projekte: ProjektUebersicht[]; statusOpts: string[] }) {
  const k = useMemo(() => {
    const byId: Record<string, number> = {};
    for (const id of STATUS_ID_ORDER) byId[id] = 0;
    const sid = (p: ProjektUebersicht) => statusIdFor(canonicalProjektStatus(p.status || "", statusOpts));
    for (const p of projekte) byId[sid(p)] = (byId[sid(p)] || 0) + 1;
    const summe = (ids: string[], f: (p: ProjektUebersicht) => number) =>
      projekte.filter((p) => ids.includes(sid(p))).reduce((a, p) => a + f(p), 0);
    return {
      count: projekte.length,
      byId,
      activeNetto: summe(["aktiv"], (p) => Number(p.nettowert || 0)),
      openRechnung: summe(["rechnung"], (p) => Number(p.nettowert || 0)),
      gewinnYTD: summe(["aktiv", "rechnung", "abgeschlossen"], computeGewinn),
    };
  }, [projekte, statusOpts]);
  const pct = (n: number) => (k.count > 0 ? Math.round((n / k.count) * 100) : 0);
  return (
    <div className="vg-proj-strip is-kompakt">
      <div className="vg-proj-strip-card is-hero">
        <div className="vg-strip-eyebrow">Portfolio Status</div>
        <div className="vg-strip-num">
          {k.count} <span className="vg-strip-num-sub">Projekte · {k.byId.aktiv || 0} aktiv</span>
        </div>
        <div className="vg-strip-bar">
          <i className="s-anfrage" style={{ width: pct(k.byId.anfrage || 0) + "%" }} />
          <i className="s-aktiv" style={{ width: pct(k.byId.aktiv || 0) + "%" }} />
          <i className="s-rechnung" style={{ width: pct(k.byId.rechnung || 0) + "%" }} />
          <i className="s-abg" style={{ width: pct(k.byId.abgeschlossen || 0) + "%" }} />
          <i className="s-verl" style={{ width: pct(k.byId.verloren || 0) + "%" }} />
        </div>
        <div className="vg-strip-legend">
          <span><i style={{ background: "var(--vg-amber)" }} />Anfrage {k.byId.anfrage || 0}</span>
          <span><i style={{ background: "var(--vg-green)" }} />Aktiv {k.byId.aktiv || 0}</span>
          <span><i style={{ background: "var(--vg-blue)" }} />Rechnung {k.byId.rechnung || 0}</span>
          <span><i style={{ background: "var(--vg-text-3)" }} />Abgeschlossen {k.byId.abgeschlossen || 0}</span>
        </div>
      </div>
      <div className="vg-proj-strip-card">
        <div className="vg-strip-eyebrow">Aktives Volumen</div>
        <div className="vg-strip-num">{fmtEUR(k.activeNetto) || "—"}</div>
        <div className="vg-strip-sub">Netto in laufenden Projekten</div>
      </div>
      <div className="vg-proj-strip-card">
        <div className="vg-strip-eyebrow">Offene Rechnungen</div>
        <div className="vg-strip-num" style={{ color: "var(--vg-blue)" }}>{fmtEUR(k.openRechnung) || "—"}</div>
        <div className="vg-strip-sub">{k.byId.rechnung || 0} Projekte in Rechnungslauf</div>
      </div>
      <div className="vg-proj-strip-card">
        <div className="vg-strip-eyebrow">Gewinn realisiert + laufend</div>
        <div className="vg-strip-num" style={{ color: "var(--vg-green)" }}>{fmtEUR(k.gewinnYTD) || "—"}</div>
        <div className="vg-strip-sub">aus Material/Stunden hochgerechnet</div>
      </div>
    </div>
  );
}
